import type { Permission } from '@clinic/contracts';
import type { PageTourStep } from '../../../shared/tour';

/**
 * The first-login welcome (employer request 2026-10-12) and its in-page tour (2026-10-13). The
 * steps come from what the member can do (permissions, never a role name), so a receptionist is not
 * shown the books and a doctor is not shown the settings. The server still checks every request.
 */

export type TourStepId =
  | 'today'
  | 'calendar'
  | 'newAppointment'
  | 'walkIn'
  | 'patients'
  | 'addPatient'
  | 'recall'
  | 'visit'
  | 'medical'
  | 'finance'
  | 'inventoryReceipt'
  | 'settingsHours'
  | 'settingsLaser'
  | 'members';
export type SetupStepId = 'laser' | 'members' | 'hours';

export interface TourStep {
  id: TourStepId;
  /** The step is shown when the member holds any of these. */
  permission: readonly Permission[];
  /** The page must be open to the member too (any of these); absent when `permission` is enough. */
  page?: readonly Permission[];
  /** The page, relative to /o/:org; a query selects the tab. */
  path: string;
  /** data-tour="…" on a real element of that page; absent = the card is centred. */
  target?: string;
}

/** In the order a day at the clinic goes. */
export const TOUR_STEPS: readonly TourStep[] = [
  { id: 'today', permission: ['appointments.read'], path: '', target: 'today-board' },
  { id: 'calendar', permission: ['appointments.read'], path: 'calendar', target: 'calendar-grid' },
  {
    id: 'newAppointment',
    permission: ['appointments.manage'],
    page: ['appointments.read'],
    path: 'calendar',
    target: 'calendar-new',
  },
  { id: 'walkIn', permission: ['appointments.manage'], page: ['appointments.read'], path: 'calendar', target: 'calendar-walk-in' },
  { id: 'patients', permission: ['patients.read'], path: 'patients', target: 'patients-list' },
  { id: 'addPatient', permission: ['patients.write'], page: ['patients.read'], path: 'patients', target: 'patients-add' },
  { id: 'recall', permission: ['visits.read'], page: ['patients.read'], path: 'patients', target: 'patients-tabs' },
  { id: 'visit', permission: ['visits.manage', 'payments.record'], page: ['appointments.read'], path: 'calendar' },
  { id: 'medical', permission: ['medical.read'], page: ['patients.read'], path: 'patients' },
  { id: 'finance', permission: ['finance.read'], path: 'finance', target: 'finance-tabs' },
  {
    id: 'inventoryReceipt',
    permission: ['inventory.receive'],
    page: ['inventory.read'],
    path: 'inventory?tab=receive',
    target: 'inventory-receive-tab',
  },
  { id: 'settingsHours', permission: ['settings.manage'], path: 'settings?tab=booking', target: 'settings-hours' },
  { id: 'settingsLaser', permission: ['settings.manage'], path: 'settings?tab=laser', target: 'settings-laser' },
  { id: 'members', permission: ['members.manage'], page: ['members.read'], path: 'members', target: 'members-add' },
];

type Can = (permission: Permission) => boolean;

/** The in-page steps this member sees. */
export function tourSteps(can: Can): TourStep[] {
  return TOUR_STEPS.filter((s) => s.permission.some(can) && (!s.page || s.page.some(can)));
}

/** The engine's steps, with the texts of each (from fa.welcomeTour.steps). */
export function pageSteps(can: Can, copy: Record<TourStepId, { title: string; body: string }>): PageTourStep[] {
  return tourSteps(can).map((s) => ({ id: s.id, path: s.path, target: s.target, title: copy[s.id].title, body: copy[s.id].body }));
}

/** The clinic admin sets the clinic up; everybody else only takes the tour. */
export function canSetUp(can: Can): boolean {
  return can('settings.manage') && can('members.manage');
}

export const SETUP_STEPS: readonly SetupStepId[] = ['laser', 'members', 'hours'];

export type WelcomePage = { kind: 'welcome' } | { kind: 'setup'; id: SetupStepId };

/** The welcome dialog: welcome, then the admin's setup (first time only). The page tour follows it. */
export function welcomePages(can: Can, options: { setup: boolean }): WelcomePage[] {
  return [
    { kind: 'welcome' },
    ...(options.setup && canSetUp(can) ? SETUP_STEPS.map((id): WelcomePage => ({ kind: 'setup', id })) : []),
  ];
}
